import React, { useCallback, useMemo, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import classNames from 'classnames';

import { RootState } from '../../../../../lib/store';
import { INVALID_VALUE } from '../../../../../lib/util/types';

import removeTrashIcon from '../../../../../assets/img/remove-trash-icon.svg';
import { ChartLine } from '../../../../../shared_components/ChartLine';
import ClickableDiv from '../../../../../shared_components/ClickableDiv/ClickableDiv';
import { UI_ROUTES } from '../../../../../util/routes';
import ClickableImage from '../../../../ClickableImage/ClickableImage';
import CompanyInfoBlock from '../../../../CompanyInfoBlock/CompanyInfoBlock';
import { getColumnCountFromGrid } from '../../../../WatchList/WatchList.constans';
import { BaseRowProps, VariantOneProps } from '../../SymbolTableRow.types';

import styles from './SymbolTableRowMobile.module.scss';

type OwnProps = BaseRowProps & {
  model: VariantOneProps;
};

type Props = OwnProps & ReduxProps;

const SWIPE_DISTANCE_LIMIT = 50;

const SymbolTableRowMobile = React.memo(
  ({
    model,
    hasSwipeableRows,
    swipedRow,
    setSwipedRow,
    onRemove,
    name,
    isWeek52Item,
    companyLogoAndName,
    oneRowInfoContent,
    gridTemplateColumns,
  }: Props) => {
    const {
      stock,
      chartSymbol,
      priceMovement,
      direction,
      onToggleShowPercent,
      changePercent,
      changePrice,
      lastPrice,
      showPercentage = true,
    } = model;

    const { symbol } = stock;
    const navigate = useNavigate();

    const symbolForChart = chartSymbol || symbol;

    const entry = companyLogoAndName[symbol];
    const url = entry?.url ?? '';
    const logo = entry?.logo;

    const columnCount = getColumnCountFromGrid(gridTemplateColumns);

    const isSwiped = hasSwipeableRows && swipedRow === symbol;

    const rowClassName = useMemo(() => {
      if (!hasSwipeableRows) return '';
      return classNames(styles.swipeableRow, { [styles.swipedLeft]: isSwiped });
    }, [hasSwipeableRows, isSwiped]);

    const contentClassName = useMemo(() => {
      if (!hasSwipeableRows) return '';
      return classNames(styles.swipeableRowContent, {
        [styles.swipeableRowSwipped]: isSwiped,
      });
    }, [hasSwipeableRows, isSwiped]);

    const touchStartX = useRef(0);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
      touchStartX.current = e.touches[0]?.clientX ?? 0;
    }, []);

    const handleTouchEnd = useCallback(
      (e: React.TouchEvent) => {
        const endX = e.changedTouches?.[0]?.clientX ?? touchStartX.current;
        const deltaX = touchStartX.current - endX;

        if (deltaX > SWIPE_DISTANCE_LIMIT) {
          setSwipedRow?.(symbol);
          return;
        }
        if (deltaX < -SWIPE_DISTANCE_LIMIT) {
          setSwipedRow?.('');
        }
      },
      [setSwipedRow, symbol],
    );

    // Only attach touch handlers when swipeable
    const touchHandlers = hasSwipeableRows
      ? { onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd }
      : undefined;

    const Wrapper: React.ElementType = hasSwipeableRows ? 'div' : React.Fragment;
    const wrapperProps = hasSwipeableRows
      ? ({ className: rowClassName, ...touchHandlers } as const)
      : undefined;

    const RowContent = (
      <div className={styles.mobileRow}>
        <ClickableDiv
          className={styles.mobileRowContent}
          onClick={() => navigate(`${UI_ROUTES.symbol}/${symbol}`)}
        >
          <CompanyInfoBlock
            item={stock}
            itemUrl={url}
            companyName={name}
            logo={logo}
            isWeek52Item={isWeek52Item}
            oneRowInfoContent={oneRowInfoContent}
            columnCount={columnCount}
          />
          {isWeek52Item ? (
            <div className={styles.columnFiftyTwoWeeksMobile}>{lastPrice}</div>
          ) : (
            <div className={styles.mobileRowChart}>
              <ChartLine symbol={symbolForChart} />
            </div>
          )}
        </ClickableDiv>

        <div className={styles.mobileRowLastPrice}>
          <div
            className={classNames(
              styles.lastPriceChanger,
              isWeek52Item
                ? {
                  [styles.priceTextMinus]: direction === '-',
                  [styles.priceTextPlus]: direction !== '-',
                }
                : {
                  [styles.noPriceChange]: priceMovement === 'equal',
                  [styles.negativeChange]: priceMovement === 'down',
                  [styles.positiveChange]: priceMovement === 'up',
                },
            )}
          >
            {lastPrice}
          </div>

          <ClickableDiv className={styles.mobileRowPercentChange} onClick={onToggleShowPercent}>
            {showPercentage ? (
              <span className={direction === '-' ? styles.priceTextMinus : styles.priceTextPlus}>
                {changePercent ?? INVALID_VALUE}
              </span>
            ) : (
              <span className={direction === '-' ? styles.priceTextMinus : styles.priceTextPlus}>
                {changePrice ?? INVALID_VALUE}
              </span>
            )}
          </ClickableDiv>
        </div>
      </div>
    );

    return (
      <Wrapper {...wrapperProps}>
        {hasSwipeableRows ? (
          <div className={contentClassName}>
            {RowContent}
            {isSwiped && (
              <ClickableImage
                src={removeTrashIcon}
                className={styles.removeButton}
                onClick={onRemove}
              />
            )}
          </div>
        ) : (
          RowContent
        )}
      </Wrapper>
    );
  },
);

const mapStateToProps = (state: RootState) => ({
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(SymbolTableRowMobile);
